import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ShieldCheck, Download, Funnel, MagnifyingGlass, ArrowRight, X } from "@phosphor-icons/react";

const SEVERITY_COLOR = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  warning: "bg-accent/15 text-accent border-accent/30",
  info: "bg-muted text-muted-foreground border-border",
};

const ACTION_LABELS = {
  create: "Création", update: "Modification", delete: "Suppression",
  archive: "Archivage", restore: "Restauration", login: "Connexion",
  publish: "Publication", cancel: "Annulation", seed: "Provisionnement",
  import_csv: "Import CSV", message_sent: "Message envoyé",
};

function AuditDiff({ log }) {
  const oldV = log.old_values || {};
  const newV = log.new_values || {};
  const keys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)])).sort();
  if (!keys.length) {
    return (
      <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">
        Cet événement n'a pas de comparaison avant/après (création, lecture, ou action simple).
      </div>
    );
  }
  const fmt = (v) => {
    if (v === null || v === undefined) return <em className="text-muted-foreground">—</em>;
    if (typeof v === "object") return <pre className="text-[10px] whitespace-pre-wrap break-all">{JSON.stringify(v, null, 2)}</pre>;
    return <span className="break-all">{String(v)}</span>;
  };
  return (
    <div className="space-y-2">
      {keys.map(k => {
        const before = oldV[k], after = newV[k];
        const changed = JSON.stringify(before) !== JSON.stringify(after);
        return (
          <div key={k} className="border border-border rounded-md overflow-hidden" data-testid={`audit-diff-${k}`}>
            <div className="px-3 py-1.5 bg-muted text-xs font-mono uppercase tracking-wide">{k}</div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-stretch">
              <div className={`p-3 text-xs ${changed ? "bg-destructive/5 border-r border-destructive/20" : "bg-muted/30 border-r border-border"}`}>
                <div className="text-[9px] uppercase tracking-wider text-destructive font-semibold mb-1">Avant</div>
                {fmt(before)}
              </div>
              <div className={`flex items-center justify-center px-2 ${changed ? "text-primary" : "text-muted-foreground/40"}`}>
                <ArrowRight size={14} />
              </div>
              <div className={`p-3 text-xs ${changed ? "bg-primary/5" : "bg-muted/30"}`}>
                <div className="text-[9px] uppercase tracking-wider text-primary font-semibold mb-1">Après</div>
                {fmt(after)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [filters, setFilters] = useState({
    module: "all", action: "all", severity: "all",
    date_from: "", date_to: "", search: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") params.set(k, v);
      });
      const [l, s] = await Promise.all([
        api.get(`/audit-logs${params.toString() ? "?" + params.toString() : ""}`),
        api.get("/audit-logs/summary"),
      ]);
      setLogs(l.data); setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const modules = useMemo(() => Array.from(new Set(logs.map(l => l.entity))).filter(Boolean).sort(), [logs]);
  const actions = useMemo(() => Array.from(new Set(logs.map(l => l.action))).filter(Boolean).sort(), [logs]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
    window.open(`${API_BASE}/audit-logs/export.csv?${params.toString()}`, "_blank");
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Sécurité & Conformité</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <ShieldCheck size={32} weight="duotone" className="text-primary" />
            Journal d'audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Traçabilité immuable de toutes les actions sensibles. Les secrets sont automatiquement masqués.</p>
        </div>
        <Button onClick={exportCsv} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-export-audit-csv">
          <Download size={16} className="mr-2" /> Exporter CSV
        </Button>
      </header>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-5" data-testid="kpi-audit-total"><div className="text-xs uppercase text-muted-foreground">Total événements</div><div className="font-heading text-3xl font-bold mt-1 tabular-nums">{summary.total}</div></Card>
          <Card className="p-5" data-testid="kpi-audit-recent"><div className="text-xs uppercase text-muted-foreground">7 derniers jours</div><div className="font-heading text-3xl font-bold mt-1 tabular-nums">{summary.last_7_days}</div></Card>
          <Card className="p-5 border-accent/40" data-testid="kpi-audit-warn"><div className="text-xs uppercase text-accent">Avertissements</div><div className="font-heading text-3xl font-bold mt-1 tabular-nums text-accent">{summary.warnings}</div></Card>
          <Card className="p-5 border-destructive/40" data-testid="kpi-audit-crit"><div className="text-xs uppercase text-destructive">Critiques</div><div className="font-heading text-3xl font-bold mt-1 tabular-nums text-destructive">{summary.critical}</div></Card>
        </div>
      )}

      <Card className="p-5 mb-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
          <Funnel size={12} weight="fill" /> Filtres
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Recherche</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-7 h-9" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
                     placeholder="Email, entité…" data-testid="filter-audit-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Module</Label>
            <Select value={filters.module} onValueChange={v => setFilters({ ...filters, module: v })}>
              <SelectTrigger className="h-9" data-testid="filter-audit-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {modules.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={filters.action} onValueChange={v => setFilters({ ...filters, action: v })}>
              <SelectTrigger className="h-9" data-testid="filter-audit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {actions.map(a => <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Sévérité</Label>
            <Select value={filters.severity} onValueChange={v => setFilters({ ...filters, severity: v })}>
              <SelectTrigger className="h-9" data-testid="filter-audit-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Avertissement</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Du</Label>
            <Input type="date" className="h-9" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-audit-from" />
          </div>
          <div>
            <Label className="text-xs">Au</Label>
            <Input type="date" className="h-9" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-audit-to" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => setFilters({ module: "all", action: "all", severity: "all", date_from: "", date_to: "", search: "" })}>Réinitialiser</Button>
          <Button size="sm" onClick={load} data-testid="btn-apply-audit-filters">Appliquer</Button>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
              <th className="px-4 py-3">Horodatage</th>
              <th className="px-4 py-3">Utilisateur</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Module</th>
              <th className="px-4 py-3">Entité</th>
              <th className="px-4 py-3">Sévérité</th>
              <th className="px-4 py-3">Détails</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Chargement…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun événement pour ces filtres.</td></tr>
            ) : logs.map((l, i) => (
              <tr key={l.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/20" : ""} hover:bg-muted/40 cursor-pointer`}
                  onClick={() => setDrawer(l)} data-testid={`audit-row-${l.id}`}>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                  {new Date(l.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" })}
                </td>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-medium">{l.user_name || l.user_email}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{l.user_role}</div>
                </td>
                <td className="px-4 py-2.5 text-xs font-mono">{ACTION_LABELS[l.action] || l.action}</td>
                <td className="px-4 py-2.5 text-xs">{l.entity}</td>
                <td className="px-4 py-2.5 text-[11px] font-mono text-muted-foreground">{(l.entity_id || "").slice(0, 12)}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${SEVERITY_COLOR[l.severity || "info"]}`}>
                    {l.severity || "info"}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[300px] truncate" title={JSON.stringify(l.meta)}>
                  {Object.entries(l.meta || {}).slice(0, 3).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="mt-3 text-xs text-muted-foreground">{logs.length} événement(s) affiché(s). Cliquez sur une ligne pour voir le diff avant/après. Les secrets sont automatiquement masqués.</div>

      {/* --- Diff drawer --- */}
      <Sheet open={!!drawer} onOpenChange={(v) => !v && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="audit-diff-drawer">
          {drawer && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <ShieldCheck size={20} weight="duotone" className="text-primary" />
                  {ACTION_LABELS[drawer.action] || drawer.action} · {drawer.entity}
                </SheetTitle>
                <SheetDescription>
                  Par <strong>{drawer.user_name || drawer.user_email}</strong> ({drawer.user_role}) · {new Date(drawer.at).toLocaleString("fr-FR")}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-0.5">Entité</div><div className="font-mono">{drawer.entity_id}</div></div>
                  <div><div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-0.5">Sévérité</div>
                    <Badge variant="outline" className={`text-[10px] ${SEVERITY_COLOR[drawer.severity || "info"]}`}>{drawer.severity || "info"}</Badge>
                  </div>
                </div>

                {drawer.meta && Object.keys(drawer.meta).length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Contexte</div>
                    <pre className="text-[11px] bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(drawer.meta, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Comparaison avant / après</div>
                  <AuditDiff log={drawer} />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
